<template>
  <el-col :span="24" id="prs" v-loading.body="(changingPage || changingTab) && !navigating" element-loading-text="Fetching page...">
    <ListViewHeader
      title="PAYROLL ENTRIES"
      ref="listViewHeader"
      :meta="meta"
      :selection="selection"
      :currentPage="currentPage"
      :changePage="changePage"
      :handleBatchUpdateSelection="handleBatchUpdateSelection"
      :batchUpdateOptions="batchUpdateOptions"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :createRouteName="'NewPr'">
    </ListViewHeader>
    <el-row type="flex" justify="left" align="middle" style="padding: 0 12px; margin: 12px 0;">
      <el-col :span="14">
        <el-row type="flex" justify="left" align="middle">
          <el-button-group>
            <el-button
              :type="activeTab === 'open' ? 'primary' : null"
              :class="activeTab === 'open' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('open')">
              View Open Payrolls
            </el-button>
            <el-button
              :type="activeTab === 'posted' ? 'primary' : null"
              :class="activeTab === 'posted' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('posted')">
              View Posted Payrolls
            </el-button>
            <el-button
              :type="activeTab === 'all' ? 'primary' : null"
              :class="activeTab === 'all' ? 'focussedBtn' : 'unfocussedBtn'"
              @click="() => changeTab('all')">
              View All Payrolls
            </el-button>
          </el-button-group>
        </el-row>
      </el-col>
      <el-col :span="20">
        <el-row type="flex" justify="right" align="middle" style="padding: 0 12px;">
          <Filters
            ref="filters"
            :filters="filters"
            :filtersVisible="showFilters"
            :filterSize="12"
            :autoApply="false"
            clearAction="clearJeState"
            clearMetadataAction="resetJeMeta"
            searchAction="fetchJesWithQuery"
            transactionType="Je"
            :lineFieldSearch="otherNameFieldEnabled ? { field: 'other_name', transactionType: 'je' } : null"
            @filterCleared="() => $refs.filters.applyFilters()">
            <template v-for="(f, i) in filters" slot-scope="props" :slot="`${f.type}${i}`">
              <component :key="i" :is="f.type" :properties="f.props" :ref="`${f.type+'_je_ref'}`"/>
            </template>
          </Filters>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <TransactionsConfList v-if="prs" name="pr_transactions_conf_list" ref="transactions_conf_list" :aggridColumns="updatedGridColumns"
          :rows="prs" :currentPage="currentPage"/>
        <el-row v-if="!meta.count" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/RetainedFilters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  // import { payload as sourcecodePayload } from '@/components/RouteComponents/Sourcecodes/actionPayload'
  import TransactionsConfList from '@/components/SigmaClientNew/TransactionsConfList.vue'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      ListViewHeader,
      TransactionsConfList
    },
    mixins: [ listViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        statusChangeModalVisible: false,
        transactionToUpdate: null,
        activeTab: 'open',
        changingTab: false,
        aggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'dateColumn', 'descriptionColumn', 'currencyColumn', 'debitsDecimalAmountColumn', 'batchColumn', 'actionsColumn']
      }
    },
    methods: {
      /**
       * Handles tab changes
       */
      async changeTab (tab) {
        try {
          this.changingTab = true
          await Promise.all([
            this.$store.dispatch('resetJeMeta'),
            this.$store.dispatch('clearJeState', true),
            this.$store.dispatch('updateTransactionSelection', [])
          ])
          const prSourcecode = this.$store.getters.getAllSourcecodes.find(sc => sc && sc.code === 'PR')
          if (!(prSourcecode && prSourcecode.id)) {
            throw new Error('Source Code of type PR not found')
          }
          let statuses = ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'VOIDED', 'CLOSED']
          switch (tab) {
            case 'open':
              statuses = ['DRAFT', 'OPEN', 'AUDITED']
              break
            case 'posted':
              statuses = ['POSTED', 'CLOSED']
          }
          let query = { status__in: statuses, source_code: prSourcecode.id }
          for (let f in this.currentFilters) {
            if (!query.hasOwnProperty(f)) {
              query[f] = this.currentFilters[f]
            }
          }
          await this.$store.dispatch('clearFilters')
          const entities = await this.$store.dispatch('fetchJesWithQuery', { query, storeLines: false })
          await this.$store.dispatch('setFilter', query)
          const promises = await this.$store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          // Update active tab in Vuex
          this.$store.dispatch('setActiveTab', tab)
        } catch (e) {
          console.log(e)
        } finally {
          this.activeTab = tab
          this.changingTab = false
        }
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selection])
      },
      /**
       * Handles paginating between PR pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          await this.$store.dispatch('setJeFilterInfo', { pageCount: page })
          // Get FilterInfo from the store data
          let filterStoreData = this.getJeFilterInfo
          if (Object.keys(filterStoreData).length) {
            this.filterRetained()
            return
          }
          this.changingPage = true
          const { currentFilters } = this
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { query: { ...currentFilters, page }, storeLines: false })
            : Object.assign({}, { query: { page }, storeLines: false })
          await this.$store.dispatch('clearJeState', [])
          const entities = await this.$store.dispatch('fetchJesWithQuery', payload)
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.$refs['transactions_conf_list'].restoreSelection(this.selection)
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      },
      async filterRetained () {
        this.changingPage = true
        // Get FilterInfo from the store data
        let filterStoreData = this.getJeFilterInfo
        if (!this.$store.getters.getAllSourcecodes.length) {
          await this.$store.dispatch('fetchSourcecodesWithQuery')
        }
        const prSourcecode = this.$store.getters.getAllSourcecodes.find(sc => sc && sc.code === 'PR')
        if (!(prSourcecode && prSourcecode.id)) {
          throw new Error('Source Code of type PR not found')
        }
        const activeTab = this.$store.getters.getActiveTab || 'all' // Default to 'all'
        let statuses = []
        switch (activeTab) {
          case 'open':
            statuses = ['DRAFT', 'OPEN', 'AUDITED']
            break
          case 'posted':
            statuses = ['POSTED', 'CLOSED']
            break
          default:
            statuses = ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'CLOSED', 'VOIDED']
            break
        }
        if (!Object.keys(filterStoreData).length) {
          const query = { status__in: statuses, source_code: prSourcecode.id }
          const entities = await store.dispatch('fetchJesWithQuery', { query, storeLines: false })
          await store.dispatch('setFilter', query)
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          this.changingPage = false
          return
        }
        try {
          const { currentFilters } = this
          // Prepare payload query to append to the API
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, {
              query: {
                ...currentFilters,
                page: filterStoreData['pageCount'] ? filterStoreData['pageCount'] : this.currentPage },
              storeLines: false,
              source_code: prSourcecode.id
            })
          : Object.assign({}, { query: { page: filterStoreData['pageCount'] ? filterStoreData['pageCount'] : this.currentPage }, storeLines: false })
          // Check 'keyword' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
            payload.query['search'] = filterStoreData['keyword']
          }
          // Check 'status__in' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
            payload.query['status__in'] = filterStoreData['status__in']
          }
          // Check 'batch' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['batch'] && filterStoreData['batch'].length > 0) {
            payload.query['batch'] = filterStoreData['batch']
          }
          // Check 'vendor' value is exist or not, if exists then assing the value
          if (filterStoreData && filterStoreData['batch__bank__currency__in'] && filterStoreData['batch__bank__currency__in'].length > 0) {
            payload.query['batch__bank__currency__in'] = filterStoreData['batch__bank__currency__in']
          }
          // Reset Prs data
          await Promise.all([
            store.dispatch('resetJeMeta'),
            store.dispatch('clearJeState', true),
            store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
            store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
          ])
          // Get Pr's data based on the filter query
          const entities = await store.dispatch('fetchJesWithQuery', payload)
          const promises = await store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          // Prefill the data for the filters
          if (this.filters && this.filters.length && this.$refs) {
            // Check 'keywordFilter_je_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.keywordFilter_je_ref && filterStoreData['keyword'] && filterStoreData['keyword'].length > 0) {
              this.$refs.keywordFilter_je_ref[0].keyword = filterStoreData['keyword']
              this.$refs.keywordFilter_je_ref[0].change()
            }
            // Check 'selectFilter_je_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.selectFilter_je_ref && filterStoreData['status__in'] && filterStoreData['status__in'].length > 0) {
              this.$refs.selectFilter_je_ref[0].selectedVal = filterStoreData['status__in']
              this.$refs.selectFilter_je_ref[0].change()
            }
            // Check 'selectFilter_je_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.selectFilter_je_ref && filterStoreData['batch__bank__currency__in'] && filterStoreData['batch__bank__currency__in'].length > 0) {
              this.$refs.selectFilter_je_ref[1].selectedVal = filterStoreData['batch__bank__currency__in']
              this.$refs.selectFilter_je_ref[1].change()
            }
            // Check 'autocompleteFilter_ref' is exist or not, if exists then assing the value to prefill it
            if (this.$refs && this.$refs.autocompleteFilter_po_ref && filterStoreData && ((filterStoreData['batch'] && filterStoreData['batch'].length > 0) || (filterStoreData['vendor'] && filterStoreData['vendor'].length > 0))) {
              // Check 'batch' data is exist or not, if exists then assing the value to prefill it
              if (filterStoreData && filterStoreData['batch']) {
                let batchInfo = this.getBatchById([filterStoreData['batch']])
                this.$refs.autocompleteFilter_je_ref[0].keyword = batchInfo.name
                this.$refs.autocompleteFilter_je_ref[0].handleSelect({label: batchInfo.name, value: batchInfo.id})
              }
            }
          }
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      }
    },
    computed: {
      ...mapGetters({
        prs: 'getJes',
        meta: 'getJeMetadata',
        currentPage: 'getCurrentJePage',
        getBatchById: 'getBatchById',
        selection: 'getSelectedTransactions',
        getCurrencyByBatchId: 'getCurrencyByBatchId',
        getJeFilterInfo: 'getJeFilterInfo',
        getActiveTab: 'getActiveTab'
      }),
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'batch__bank__currency',
              clearable: true,
              opts: this.$store.getters.getAllCurrencies.map(c => ({ label: c.code, value: c.id })),
              multi: true,
              placeholder: 'Currency',
              styles: '0 4px'
            }
          }
        ]
      },
      updatedGridColumns () {
        return (this.activeTab === 'posted') ? this.aggridColumns.filter((c) => c !== 'rowSelect') : this.aggridColumns
      }
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('prs').offsetHeight
          const headerHeight = document.getElementById('list-view-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    },
    created () {
      this.activeTab = this.getActiveTab
    },
    beforeDestroy () {
      this.$store.dispatch('updateTransactionSelection', [])
      this.$store.dispatch('updateFilteredTransactionSelection', [])
      // Reset activeTab to 'open' when the component is destroyed
    },
    async beforeRouteEnter (to, from, next) {
      try {
        if (!(from.name === 'EditPr' && to.name === 'PRs')) {
          await Promise.all([store.dispatch('clearSetJeFilterInfo')])
          await store.dispatch('resetActiveTab')
        }
        if (from.name === 'EditPr' && to.name === 'PRs') {
          const activeTab = store.getters.getActiveTab || 'all' // Default to 'all'
          // Determine statuses based on the active tab
          const prSourcecode = store.getters.getAllSourcecodes.find(sc => sc && sc.code === 'PR')
          if (!(prSourcecode && prSourcecode.id)) {
            throw new Error('Source Code of type PR not found')
          }
          let statuses = ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'VOIDED', 'CLOSED']
          switch (activeTab) {
            case 'open':
              statuses = ['DRAFT', 'OPEN', 'AUDITED']
              break
            case 'posted':
              statuses = ['POSTED', 'CLOSED']
              break
          }
          const query = { status__in: statuses, source_code: prSourcecode.id }
          await store.dispatch('setFilter', query)
          return
        } await Promise.all([
          store.dispatch('resetJeMeta'),
          store.dispatch('clearJeState', true),
          store.dispatch('fetchUiSetting', { name: 'LINE_FIELD_ORDER' }),
          store.dispatch('fetchUiSetting', { name: 'CONFIRM_ON_POST' })
        ])
        // const payload = Object.assign({}, sourcecodePayload, { query: { code: 'PR' } })
        // await Promise.all([store.dispatch('resetJeMeta'), store.dispatch('clearJeState', true)])
        // await store.dispatch('fetchWithQuery', payload)
        // const prSourcecode = store.getters.getAllSourcecodes.find(sc => sc && sc.code === 'PR')
        // if (!(prSourcecode && prSourcecode.id)) {
        //   throw new Error('Source Code of type PR not found')
        // }
        // const query = { status__in: ['DRAFT', 'OPEN', 'AUDITED'], source_code: prSourcecode.id }
        // const entities = await store.dispatch('fetchJesWithQuery', { query, storeLines: false })
        // await store.dispatch('setFilter', query)
        // const promises = await store.dispatch('findDependencies', entities)
        // await Promise.all(promises)
      } catch (e) {
        console.log(e)
      } finally {
        next(vm => {
          vm.filterRetained()
        })
      }
    }
  }
</script>

<style scoped>
  .focussed-btn {
    background-color: #04709d;
    cursor: default;
  }
  .unfocussed-btn {
    background-color: #fff;
    cursor: pointer;
  }
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .el-button--info.is-plain {
    background: #e1f5fe;
    border: 1px solid #bfcbd9;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Prs/Prs.vue